import { useState } from 'react'
import { invitationsApi, ApiError, type PendingInvitation } from '@/lib/invitations-api'
import { trackEvent, Events } from '@/lib/observability'

interface PendingInvitationsProps {
  invitations: PendingInvitation[]
  onAccepted: (invitation: PendingInvitation) => void
  onDeclined: (invitation: PendingInvitation) => void
}

export function PendingInvitations({ invitations, onAccepted, onDeclined }: PendingInvitationsProps) {
  const [processingId, setProcessingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleAccept = async (invitation: PendingInvitation) => {
    setProcessingId(invitation.id)
    setError(null)

    try {
      await invitationsApi.acceptInvitation(invitation.token)
      trackEvent(Events.INVITATION_ACCEPTED)
      onAccepted(invitation)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to accept invitation')
    } finally {
      setProcessingId(null)
    }
  }

  const handleDecline = async (invitation: PendingInvitation) => {
    setProcessingId(invitation.id)
    setError(null)

    try {
      await invitationsApi.declineInvitation(invitation.token)
      trackEvent(Events.INVITATION_DECLINED)
      onDeclined(invitation)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to decline invitation')
    } finally {
      setProcessingId(null)
    }
  }

  if (invitations.length === 0) {
    return null
  }

  return (
    <div className="mb-6 rounded-lg border border-primary-200 bg-primary-50 p-4">
      <h2 className="mb-3 flex items-center gap-2 text-lg font-medium text-primary-900">
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        Pending Invitations ({invitations.length})
      </h2>

      {error && (
        <div className="mb-3 rounded bg-red-100 p-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="space-y-3">
        {invitations.map((invitation) => (
          <div
            key={invitation.id}
            className="rounded-lg border border-primary-200 bg-white p-3"
          >
            <p className="font-medium text-gray-900 ph-no-capture">
              {invitation.childName}
            </p>
            <p className="text-sm text-gray-500 ph-no-capture">
              From: {invitation.childminderEmail}
            </p>
            <p className="mb-3 text-xs text-gray-400">
              Expires: {new Date(invitation.expiresAt).toLocaleDateString()}
            </p>

            <div className="flex gap-2">
              <button
                onClick={() => handleAccept(invitation)}
                disabled={processingId === invitation.id}
                className="flex-1 rounded bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {processingId === invitation.id ? 'Processing...' : 'Accept'}
              </button>
              <button
                onClick={() => handleDecline(invitation)}
                disabled={processingId === invitation.id}
                className="flex-1 rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Decline
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
